import { BORDER_COLORS, ICON_COLORS, SURFACE_COLORS, TEXT_COLORS } from '@constants/colors.ts';
import {
    PATHWAY_CATEGORIES,
    PATHWAY_TYPES,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/ui/form/constants.ts';
import { EVENT_SOURCES } from '@shared/constants/eventTypes';
import styled from 'styled-components';

interface StyledCardProps {
    $type: string;
    $source: string;
    $hasIcon?: boolean;
}

export const Pill = styled.div<StyledCardProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-2xs, 4px);
    border-radius: var(--border-radius-round, 48px);
    ${({ $hasIcon }) =>
        $hasIcon &&
        `padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px) var(--spacing-2xs, 4px) var(--spacing-2xs, 4px);`}
    ${({ $source, $hasIcon }) => {
        if (!$hasIcon)
            switch ($source) {
                case PATHWAY_TYPES.LEARNING:
                case PATHWAY_TYPES.ASSESSMENT:
                case PATHWAY_TYPES.SURVEY:
                case EVENT_SOURCES.TASK_EVALUATION:
                case PATHWAY_CATEGORIES.TOPIC:
                case PATHWAY_CATEGORIES.TASK:
                case PATHWAY_CATEGORIES.COURSE:
                    return `
                padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px);    
                border: var(--border-width-sm-1, 1px) solid ${BORDER_COLORS.default};
                background: ${BORDER_COLORS.neutral};
            `;
                case 'New':
                    return `
                padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px);    
                border: var(--border-width-sm-1, 1px) solid ${BORDER_COLORS.default};
                background: ${BORDER_COLORS.purple};
                color: ${TEXT_COLORS.onAction};
            `;
                case 'System':
                    return `
                padding: var(--spacing-2xs, 4px) var(--spacing-xs, 8px);    
                border: var(--border-width-sm-1, 1px) solid ${BORDER_COLORS.default};
                background: ${BORDER_COLORS.neutral};                
                color: ${TEXT_COLORS.body};
                `;
            }
    }}
    ${({ $type, $hasIcon }) => {
        if ($hasIcon) {
            switch ($type) {
                case 'Assigned':
                case 'TaskExempted':
                    return `
                border: var(--border-width-sm-1, 1px) solid ${SURFACE_COLORS.action};
                background: ${SURFACE_COLORS.information};
                color:${TEXT_COLORS.poweredBy};

                `;
                case 'Completed':
                case 'TaskClosed':
                case 'Passed':
                    return `
                border: var(--border-width-sm-1, 1px) solid ${ICON_COLORS.success};
                background: ${SURFACE_COLORS.success};
                color:${TEXT_COLORS.success};
            `;
                case 'TaskRedo':
                case 'TaskAssessorChanged':
                    return `
                border: var(--border-width-sm-1, 1px) solid ${ICON_COLORS.warning};
                background: ${SURFACE_COLORS.warning};
            `;
                case 'Failed':
                case 'Aborted':
                case 'Overdue':
                case 'TaskRecalledCrewSignOff':
                case 'TaskRecalledAssessorSignOff':
                case 'TaskRecalledElementUpdated':
                case 'TaskRecalledElementDeleted':
                case 'TaskRecalledAssessorUnavailable':
                case 'TaskExemptionRejected':
                    return `
                border: var(--border-width-sm-1, 1px) solid ${ICON_COLORS.error};
                background: ${SURFACE_COLORS.error};
                color:${TEXT_COLORS.error};

            `;
                case 'Reminder':
                case 'FinalReminder':
                case 'TaskReceivedForEvaluation':
                case 'TaskReceivedForExemption':
                case 'TaskRecalledByAssessee':
                    return `
                border: var(--border-width-sm-1, 1px) solid ${ICON_COLORS.warning};
                background: ${SURFACE_COLORS.warning};
                color: ${TEXT_COLORS.warning};
            `;
                case 'Info':
                case 'Security':
                case 'Maintenance':
                    return `
                border-radius: var(--border-radius-round, 48px);
                border: var(--border-width-sm-1, 1px) solid ${BORDER_COLORS.default};
                background: ${BORDER_COLORS.neutral}`;
                case 'LearningReassignedCategoryChanged':
                    return `
                background: ${SURFACE_COLORS.purple};
                border: var(--border-width-sm-1, 1px) solid ${BORDER_COLORS.purple};
                color: ${TEXT_COLORS.purple};
            `;
            }
        }
    }}
`;

export const PillText = styled.div<StyledCardProps>`
    font-family: var(--type-font-family, Poppins), serif;
    font-size: 10px;
    font-weight: 500;
    line-height: var(--line-height-body-xs, 16px);
    ${({ $type }) => {
        switch ($type) {
            case 'Completed':
            case 'Passed':
                return `
            color: ${ICON_COLORS.success};
        `;
        }
    }}
`;

export const PillIcon = styled.span<StyledCardProps>`
    display: flex;
    padding: 3px;
    align-items: center;
    gap: 10px;
    border-radius: var(--border-radius-round, 48px);
    ${({ $type }) => {
        switch ($type) {
            case 'Completed':
            case 'Passed':
            case 'TaskClosed':
                return `
            background: ${ICON_COLORS.success};
            stroke: white;
            color: ${TEXT_COLORS.success};
        `;
            case 'TaskExempted':
                return `
            background: ${ICON_COLORS.success};
            stroke: white;
            color: ${TEXT_COLORS.poweredBy};
        `;
            case 'TaskRecalledByAssessee':
                return `
            background: ${ICON_COLORS.warning};
            stroke: white;
            color: ${TEXT_COLORS.warning};
        `;
            case 'LearningReassignedCategoryChanged':
                return `
            svg{
                stroke: ${BORDER_COLORS.white};
                background: ${ICON_COLORS.purple};
                } 
            background: ${ICON_COLORS.purple};
            `;
        }
    }}
    ${({ $type, $source }) => {
        if ($source === 'System') {
            return `background: ${ICON_COLORS.body}`;
        }
        switch ($type) {
            case 'Assigned':
            case 'TaskExempted':
                return `
            background: ${TEXT_COLORS.action};
        `;
            case 'Completed':
            case 'Passed':
            case 'TaskClosed':
                return `
            background: ${ICON_COLORS.success};
        `;
            case 'Failed':
            case 'Aborted':
            case 'Overdue':
            case 'TaskRecalledCrewSignOff':
            case 'TaskRecalledAssessorSignOff':
            case 'TaskRecalledElementUpdated':
            case 'TaskRecalledElementDeleted':
            case 'TaskRecalledAssessorUnavailable':
            case 'TaskExemptionRejected':
                return `background: ${ICON_COLORS.error};`;
            case 'Reminder':
            case 'FinalReminder':
            case 'TaskRedo':
            case 'TaskAssessorChanged':
            case 'TaskReceivedForEvaluation':
            case 'TaskReceivedForExemption':
            case 'TaskRecalledByAssessee':
                return `background: ${ICON_COLORS.warning}`;
            case 'Info':
                return `background: ${ICON_COLORS.primary}`;
            case 'LearningReassignedCategoryChanged':
                return `
            stroke: ${BORDER_COLORS.purple}
            background: ${SURFACE_COLORS.purple};

            `;
        }
    }}
`;
